import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { Button, PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { ErrorMessage, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Text } from "src/components/base/text";
import { UUID } from "src/api/api_common";
import { GetSingleSubnetResponse } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";
import { Checkbox, CheckboxField } from "src/components/base/checkbox";
import { ServerStackIcon } from "@heroicons/react/20/solid";

/**
 * The properties for {@link EnableSubnetDialog}
 */
export type EnableSubnetDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback for closing the popup */
    onClose: () => void;
    /** Callback for successful enable */
    onEnable: () => void;
    /** The subnet */
    subnet:
        | {
              /** UUID of the subnet */
              uuid: UUID;
          }
        | undefined;

    /** Optional device that should route this subnet */
    router?: UUID;
};

/**
 * Dialog to disable a given subnet
 */
export default function EnableSubnetDialog(props: EnableSubnetDialogProps) {
    const [t] = useTranslation("dialog-enable-subnet");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            routers: Array<boolean>(),
        },
        validators: {
            onChange: ({ value }) => {
                if (!value.routers.some((x) => x)) {
                    return {
                        fields: {
                            routers: t("error.selection-necessary"),
                        },
                    };
                }
            },
            onSubmitAsync: async ({ value }) => {
                if (!subnet) return;

                await ApiInternal.subnets.enable(subnet.uuid, {
                    routers: subnet.possible_routers.filter((_, i) => value.routers[i]).map((x) => x.device.uuid),
                });

                props.onEnable();
            },
        },
    });

    const [subnet, setSubnet] = React.useState<GetSingleSubnetResponse>();
    React.useEffect(() => {
        const subnet_uuid = props.subnet?.uuid;
        if (!subnet_uuid) return;
        ApiInternal.subnets.getSingle(subnet_uuid).then((subnet) => {
            setSubnet(subnet);
            if (subnet.possible_routers.length == 1) {
                form.setFieldValue("routers", [true]);
            } else {
                form.setFieldValue(
                    "routers",
                    subnet.possible_routers.map(() => false),
                );
            }
        });
    }, [props.subnet?.uuid]);

    if (!subnet) return null;

    return (
        <Dialog
            open={props.isOpen}
            onClose={() => {
                setSubnet(undefined);
                props.onClose();
            }}
        >
            <DialogTitle>{t("heading.select-router-device")}</DialogTitle>
            <Form onSubmit={form.handleSubmit}>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <Text>{t("description.select-router-device")}</Text>

                            <form.Field name={"routers"} mode={"array"}>
                                {(fieldApi) => {
                                    if (fieldApi.state.value.length === 0) {
                                        return <ErrorMessage>{t("error.no-router-available")}</ErrorMessage>;
                                    }
                                    return (
                                        <>
                                            {fieldApi.state.value.map((_, index) => (
                                                <form.Field key={index} name={`routers[${index}]`}>
                                                    {(subFieldApi) => (
                                                        <CheckboxField>
                                                            <Checkbox
                                                                checked={subFieldApi.state.value}
                                                                onChange={subFieldApi.handleChange}
                                                            />
                                                            <Label className={"flex gap-1"}>
                                                                <ServerStackIcon className={"size-5"} />
                                                                <span>
                                                                    {subnet.possible_routers[index].device.dnsname}
                                                                </span>
                                                            </Label>
                                                        </CheckboxField>
                                                    )}
                                                </form.Field>
                                            ))}
                                            {fieldApi.state.meta.errors.map((error) => (
                                                <ErrorMessage>{error}</ErrorMessage>
                                            ))}
                                        </>
                                    );
                                }}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </DialogBody>
                <DialogActions>
                    <Button onClick={props.onClose}>{tg("button.cancel")}</Button>
                    <PrimaryButton type={"submit"}>{t("button.enable-subnet")}</PrimaryButton>
                </DialogActions>
            </Form>
        </Dialog>
    );
}
